import { reactive } from "vue";
import _ from "lodash";
import { defineStore } from "pinia";
import { AxiosResponse } from "axios";
import { useToast } from "@profeat/ui-kit";

import { BroadcastStatistic, BroadcastStatisticLocal } from "~/entities/broadcast-statistic";
import { Message } from "~/entities/message";
import { WabaChannelTemplate, HeaderFileFormat, withFile, TemplateComponentFormat } from "~/entities/template";
import { api } from "~/api";
import { upload } from "~/composables/upload";
import { dateFormatOptions } from "~/utils/format";
import { $t } from "~/i18n";

import { BroadcastDelay } from "~/components/broadcastV2/types/broadcast-delay";
import { toFormData } from "~/utils/content-type";
import { BroadcastVariables, BroadcastVariablesMap } from "~/entities/broadcast";
import { Channel } from "~/entities/channel";
import { StatisticsListResponse } from "~/api/messages/broadcast/statistics-list.response";
import { replaceTemplateVariables } from "~/views/trigger/utils/template";
import { useContacts } from "./contacts.store";
import { NotificationType, useNotification } from "./notification.store";
import { useChannels } from "./channel.store";
import { useTemplates } from "./template.store";

const EXTRACT_VARS_REGEXP = /(\[\[).*?(]])/g;

export interface FileData {
  fileContent: string;
  fileMimetype: string;
  fileName: string;
  fileExtension?: string;
  fileBinary: File;
}

interface State {
  options: {
    templateId: string;
    title: string;
  };
  csvNames: string[];
  handTexts: Record<string, string | null>;
  values: string[];
  statistics: BroadcastStatisticLocal[];
  customersFile?: File;
  headerFile?: File | null;
  headerFileName?: string;
  headerPreview?: any;
  visibleSentModal: boolean;
  visibleConfirmModal: boolean;
  visiblePreviewModal: boolean;
  isFetching: boolean;
  inputErrors: Record<string, string>;
  previewMessageError: string;
  customErrors: Record<string, string>;
  customSelectError: Record<string, string>;
  title: string;
  selectedTemplate: WabaChannelTemplate | null;
  headerFileV2: FileData | null;
  selectedFieldIds: string[];
  isSimpleVariable: boolean;
  validationError: {
    headerFile: boolean;
  } & Record<string, boolean>;
  selectedChatBotId: string;
  activeUserCount: number;
  delay: BroadcastDelay | null;
  bodyVars: Record<string, string>;
  headerVars: Record<string, string>;
  buttonVars: Record<string, string>;
  footerVars: Record<string, string>;
  variablesHasErrors: boolean;
}

const newState = (): State => ({
  options: reactive({ title: "", templateId: "" }),
  csvNames: [],
  handTexts: {},
  values: [],
  statistics: [],
  visibleSentModal: false,
  visibleConfirmModal: false,
  visiblePreviewModal: false,
  isFetching: false,
  headerFile: null,
  headerFileName: "",
  headerPreview: null,
  inputErrors: {},
  previewMessageError: "",
  customErrors: {},
  customSelectError: {},
  // V2
  selectedTemplate: null,
  headerFileV2: null,
  selectedFieldIds: [],
  title: "",
  isSimpleVariable: false,
  validationError: {
    bodyEmpty: false,
    headerFile: false,
    buttonEmpty: false,
  },
  selectedChatBotId: "",
  activeUserCount: 0,
  delay: null,
  bodyVars: {},
  headerVars: {},
  buttonVars: {},
  footerVars: {},
  variablesHasErrors: false
});

const { showToast } = useToast();

function joinChannel<T>(obj: T): T & { channel: Channel | null } {
  const store = useChannels();
  const cpy = JSON.parse(JSON.stringify(obj));
  cpy.channel = store.channels.find(c => c.subscriptionId == cpy.subscriptionId) || null;

  return cpy;
}

export const useBroadcast = defineStore("broadcast", {
  state: newState,

  actions: {
    // V2
    setSelectedTemplate(payload: WabaChannelTemplate | null) {
      this.selectedTemplate = payload;
      this.clearVariables();
    },
    setHeaderFileV2(payload: FileData | null) {
      this.headerFileV2 = payload;
    },
    setValidationError(key: string, payload: boolean) {
      // @ts-ignore
      this.validationError[key] = payload;
    },
    pushSelectedFieldId(payload: string) {
      this.selectedFieldIds.push(payload);
    },
    clearSelectedFieldId() {
      this.selectedFieldIds = [];
    },
    setTitle(payload: string) {
      this.title = payload;
    },
    setSimpleVariable(payload: boolean) {
      this.isSimpleVariable = payload;
    },
    setCustomError(guid: string, payload: string) {
      this.customErrors[guid] = payload;
    },

    deleteCustomError(guid: string) {
      delete this.customErrors[guid];
    },

    initialize() {
      if (this.templateNames.length == 0) {
        this.handTexts = {};
        return;
      }

      this.handTexts = _.zipObject(
        this.templateNames,
        _.map(this.templateNames, () => "")
      );
    },

    syncVariables(variables: {
      bodyVars?: Record<string, string>,
      headerVars?: Record<string, string>,
      buttonVars?: Record<string, string>,
      footerVars?: Record<string, string>
    }) {
      Object.assign(this.bodyVars, variables.bodyVars || {})
      Object.assign(this.headerVars, variables.headerVars || {})
      Object.assign(this.buttonVars, variables.buttonVars || {})
      Object.assign(this.footerVars, variables.footerVars || {})
    },

    clearVariables() {
      this.bodyVars = {};
      this.headerVars = {};
      this.buttonVars = {};
      this.footerVars = {};
    },

    setVariablesHasErrors(hasErrors: boolean) {
      this.variablesHasErrors = hasErrors;
    },

    async reset() {
      Object.assign(this, newState());
    },

    /**
     * Deprecated, удалить ссылки
     * **/
    async checkBeforeCreate(): Promise<boolean> {
      return true;
    },

    async create({ fake }: { fake?: boolean }) {
      const notification = useNotification();

      if (!this.selectedTemplate) {
        notification.pushNotification({
          message: $t("broadcast.errors.noCustomers"),
          type: NotificationType.warning,
        });
        return;
      }

      const customerIds = await getCustomerIds();

      if (!customerIds.length) {
        notification.pushNotification({
          message: $t("broadcast.errors.noCustomers"),
          type: NotificationType.error,
        });
        return;
      }

      const fieldIds = Array.from(new Set(this.selectedFieldIds));
      const channelsStore = useChannels();
      const selectedChannel = channelsStore.selectedWabaChannel;

      if (!selectedChannel) {
        notification.pushNotification({
          message: $t("broadcast.send busy"),
          type: NotificationType.warning,
        });
        return;
      }

      const res = await upload(
        [this.headerFileV2 ? this.headerFileV2.fileBinary : undefined],
        ["headerFile"],
        "broadcasts",
        {
          title: this.title,
          templateId: this.selectedTemplate.id,
          templateBody: replaceTemplateVariables(
            this.selectedTemplate.body,
            this.variablesMap.bodyVars
          ),
          templateHeader: replaceTemplateVariables(
            this.selectedTemplate.header,
            this.variablesMap.headerVars
          ),
          variables: this.variables,
          customerIds,
          fieldIds,
          botId: this.selectedChatBotId || null,
          delay: this.delay,
          subscriptionId: selectedChannel.subscriptionId,
          fake,
        }
      );

      this.onErrBroadcastSend(res);

      async function getCustomerIds() {
        let customerIds: string[] = [];
        const contactStore = useContacts();
        const exclusions = contactStore.getInclusionsExclusions();
        if (Array.isArray(exclusions.included) && exclusions.included.length) {
          customerIds = exclusions.included;
        } else {
          customerIds = await api.customers.getContactIds(contactStore.filterBuilder.filter);
          customerIds = customerIds.filter(id => {
            if (Array.isArray(exclusions.excluded)) {
              return !exclusions.excluded.includes(id);
            }
            return id;
          })
        }
        return customerIds;
      }
    },

    async sendPreviewMsg(phoneNumber: string) {
      const notification = useNotification();
      const contactsStore = useContacts();
      const channelsStore = useChannels();

      if (this.isFetching) {
        notification.pushNotification({
          message: $t("broadcast.send busy"),
          type: NotificationType.warning,
        });
        return;
      }

      this.setFetching(true);

      const wabaCurrent = channelsStore.selectedWabaChannel;

      if (wabaCurrent === null) {
        notification.pushNotification({
          message: $t("broadcast.channel not found"),
          type: NotificationType.error,
        });
        this.setFetching(false);
        return;
      }

      const selectedTemplate = this.selectedTemplate;

      if (selectedTemplate === null) {
        notification.pushNotification({
          message: $t("broadcast.template not selected"),
          type: NotificationType.error,
        });
        this.setFetching(false);
        return;
      }

      const formData = toFormData({
        subscriptionId: wabaCurrent.subscriptionId,
        templateId: selectedTemplate.id,
        variables: {
          bodyVars: Object.values(this.bodyVars),
          headerVars: Object.values(this.headerVars),
          footerVars: Object.values(this.footerVars),
          buttonVars: Object.values(this.buttonVars),
        },
        phoneNumber,
        botId: this.selectedChatBotId || null,
        filters: contactsStore.filterBuilder.filter,
      });

      if (this.headerFileV2?.fileBinary) {
        formData.append('headerFile', this.headerFileV2?.fileBinary,)
      }

      try {
        await api.broadcasts.sendPreviewMessage(formData);
      } catch (err) {
        showToast({ color: "error", text: $t("broadcast.errors.sendSelfError") });
        return { success: false, error: err };
      } finally {
        this.setFetching(false);
      }
    },

    async fetchStatistics(params?: { broadcastId: string }): Promise<BroadcastStatisticLocal[]> {
      const statisticsList: StatisticsListResponse[] = await api.broadcasts.getStatistics(params);
      return statisticsList.map(joinChannel<StatisticsListResponse>);
    },

    async loadStatistics() {
      const data = await this.fetchStatistics();
      this.statistics = _.orderBy(data, b => new Date(b.createdAt).getTime(), ["desc"]);
    },

    async updateStatistic(broadcastId: string) {
      const index = this.statistics.findIndex(
        (s) => s.broadcastId === broadcastId
      );
      if (index >= 0) {
        const data = await this.fetchStatistics({ broadcastId });
        const newStat = data.find(b => b.broadcastId === broadcastId)
        if (newStat) {
          this.statistics[index] = newStat;
        }
      }
    },

    async setCustomersFile(file: File) {
      const { data } = await upload(
        [file],
        ["csvFile"],
        "customers/parse-first"
      );

      if (data?.names && data.names.length) {
        this.csvNames = [...(data.names as string[])];
      } else {
        this.csvNames = [];
      }

      this.customersFile = file;
    },

    async stopSendingBroadcast(broadcastId: string) {
      try {
        const res = await api.broadcasts.stopSendingBroadcast(broadcastId)
        await this.updateStatistic(broadcastId)
        return res.data
      } catch (error) {
        showToast({ color: "error", text: $t("broadcast.stopSendingError") });
        return false
      }


    },

    removeCustomersFile() {
      this.csvNames = [];
      this.customersFile = undefined;
    },

    setHeaderFile(file: File) {
      this.headerFile = file;

      const reader = new FileReader();

      reader.addEventListener("load", () => {
        this.headerPreview = reader.result;
      });

      reader.readAsDataURL(file);
    },

    removeHeaderFile() {
      this.headerFile = null;
      this.headerPreview = null;
      this.headerFileName = "";
    },

    onUpdateMessage(message: Message) {
      this.updateStatistic(message.broadcastId);
    },

    setDefaultTitle() {
      this.options.title = `Новая рассылка ${new Date().toLocaleString(
        "ru",
        dateFormatOptions
      )}`;
    },

    setDefaultTemplate() {
      this.options.templateId = this.templates[0]?.id || "";
    },

    setVisibleSentModal(payload: boolean) {
      this.visibleSentModal = payload;
    },

    setVisibleConfirmModal(payload: boolean) {
      this.visibleConfirmModal = payload;
    },

    setVisiblePreviewModal(payload: boolean) {
      this.visiblePreviewModal = payload;
    },

    setFetching(payload: boolean) {
      this.isFetching = payload;
    },

    setFileName(payload: string) {
      this.headerFileName = payload;
    },

    setPreviewMsgError(payload: string) {
      this.previewMessageError = payload;
    },

    getFileFormat(payload: TemplateComponentFormat) {
      return HeaderFileFormat[payload];
    },

    onErrBroadcastSend(res: AxiosResponse) {
      if (res.status > 300) {
        const code = res.data.error?.code;
        const error = res.data.error;

        const errPayload = {
          text: error?.message ?? code,
          color: "error",
        };

        if (code === "LengthExceeded") {
          errPayload.text = $t("broadcast.lengthExceeded", {
            cnt: error.payload.length,
            max: error.payload.maxLength,
          }) as string;
        } else if (code === "VariablesNotProvided") {
          errPayload.text = $t("broadcast.variableNotProvided");
        } else if (code === "HeaderFileError") {
          const isNotProvided = error?.message?.includes("not provided");

          if (isNotProvided) {
            errPayload.text = $t("broadcast.headerFileNotProvided");
          }
        } else if (code === "headerLengthLong") {
          errPayload.text = $t("broadcast.headerLengthLong");
        }

        // @ts-ignore
        showToast(errPayload);
        return;
      }

      showToast({ color: "success", text: $t("broadcast.successCreate") });
    },

    onErrPreviewMsgSend(res: AxiosResponse) {
      if (Array.isArray(res.data?.error?.message)) {
        res.data.error.message.forEach((message: string) => {
          this.setPreviewMsgError(message);
        });
      }

      if (this.previewMessageError.length) return;

      this.onErrBroadcastSend(res);
    },

    defineError() {
      const obj: Record<string, string> = {};
      for (const item of this.templateNames) {
        obj[item] = "";
      }
      return obj;
    },

    resetError() {
      this.customSelectError = {};
      this.customErrors = {};

      this.clearVariables();
    },

    createError() {
      this.customSelectError = {};
    },

    checkHeaderFile() {
      if (this.hasFile && !this.headerFile) {
        const notificationStore = useNotification();
        notificationStore.pushNotification({
          message: $t("broadcast.headerFileNotProvided"),
          type: NotificationType.error,
        });
      }
    },

    updateInputErrors() {
      this.inputErrors = {};

      for (const name of this.templateNames) {
        this.inputErrors[name] = $t("validation.required");
      }
    },

    updateCustomErrors() {
      const contactsStore = useContacts();
      this.customSelectError = {};

      for (const guid in this.customErrors) {
        const fieldId = this.customErrors[guid];
        if (contactsStore.getNoValueFieldMap[fieldId]) {
          this.customSelectError[guid] = $t("broadcast.noValue");
        }

        if (fieldId !== "") continue;

        this.customSelectError[guid] = $t("validation.required");
      }
    },

    checkVariablesErrors() {
      this.checkHeaderFile();
      this.updateCustomErrors();
      this.updateInputErrors();
    },

    updateDelay(delay: BroadcastDelay | null) {
      this.delay = delay;
    },
  },

  getters: {
    getActiveUserCount(): number {
      return this.activeUserCount;
    },
    // V2
    getSelectedTemplate(): WabaChannelTemplate | null {
      return this.selectedTemplate;
    },
    getHeaderFileV2(): FileData | null {
      return this.headerFileV2;
    },
    hasValidationError(): Record<string, boolean> {
      return this.validationError;
    },
    isSingleVariable(): boolean {
      return this.isSimpleVariable;
    },
    getInputErrors(): Record<string, string | null> {
      return this.inputErrors;
    },

    getCustomError(): Record<string, string> {
      return this.customSelectError;
    },

    hasError(): boolean {
      return (
        Object.values(this.customSelectError).some(
          (el) =>
            el === $t("validation.required") || el === $t("broadcast.noValue")
        ) ||
        Object.values(this.inputErrors).some(
          (el) => el === $t("validation.required")
        ) ||
        (this.hasFile && !this.headerFile)
      );
    },

    hasFile(): boolean {
      if (!this.template) {
        return false;
      }

      return withFile(this.template);
    },

    templateSelected(): boolean {
      return !!this.options?.templateId;
    },

    templates(): WabaChannelTemplate[] {
      const templatesStore = useTemplates();
      return templatesStore.templates;
    },

    template(): WabaChannelTemplate | undefined {
      if (!this.options?.templateId) {
        return undefined;
      }
      return this.templates.find(
        (template) => template.id === this.options.templateId
      );
    },

    templateCode(): string | undefined {
      return this.template?.body;
    },

    previewComponents(): any[] {
      return [];
    },

    textComponents(): any[] {
      return this.previewComponents.filter(
        (c) => c.type !== "BUTTONS"
      );
    },

    buttonComponents(): any[] {
      return this.previewComponents.filter(
        (c) => c.type === "BUTTONS"
      );
    },

    templateNames(): string[] {
      const matches = this.templateCode
        ?.match(EXTRACT_VARS_REGEXP)
        ?.map((match) => match.replaceAll("[[", "").replaceAll("]]", ""));
      return Array.from(new Set(matches)).filter(
        (n) => n !== "headerContentUrl"
      );
    },

    getVisibleSentModal(): boolean {
      return this.visibleSentModal;
    },

    getVisibleConfirmModal(): boolean {
      return this.visibleConfirmModal;
    },

    getVisiblePreviewModal(): boolean {
      return this.visiblePreviewModal;
    },

    broadcastTitles(state): string[] {
      return state.statistics.map((x) => x.broadcastTitle);
    },

    titleAlreadyExists(state): boolean {
      return this.broadcastTitles.includes(state.title.trim());
    },

    variables(state): BroadcastVariables {
      return {
        bodyVars: Object.values(state.bodyVars),
        footerVars: Object.values(state.footerVars),
        headerVars: Object.values(state.headerVars),
        buttonVars: Object.values(state.buttonVars)
      }
    },

    variablesMap(state): BroadcastVariablesMap {
      return {
        bodyVars: state.bodyVars,
        footerVars: state.footerVars,
        headerVars: state.headerVars,
        buttonVars: state.buttonVars
      }
    },

    hasErrors(state): boolean {
      return state.variablesHasErrors || !state.selectedTemplate ||
        this.titleAlreadyExists || state.title.length < 3
    }
  },
});
